import { __extends } from "tslib";
import { EventDispatcher } from '../events/EventDispatcher';
import { release } from '@awayfl/swf-loader';
import { Event } from '../events/Event';
import { SoundMixer } from './SoundMixer';
/**
 * Dispatched when a sound has finished playing.
 * @eventType	flash.events.Event.SOUND_COMPLETE
 * [Event(name="soundComplete", type="flash.events.Event")]
 *
 * The SoundChannel class controls a sound in an application. Every sound
 * is assigned to a sound channel, and the application can have multiple
 * sound channels that are mixed together.
 * The SoundChannel class contains a <codeph class="+ topic/ph pr-d/codeph ">stop()</codeph> method,
 * properties for monitoring the amplitude (volume) of the channel, and a property for assigning a
 * SoundTransform object to the channel.
 */
var SoundChannel = /** @class */ (function (_super) {
    __extends(SoundChannel, _super);
    function SoundChannel() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    // for AVM1:
    SoundChannel.prototype.axCallPublicProperty = function (value1, value2) {
        return null;
    };
    SoundChannel.prototype.axGetPublicProperty = function (value) {
        return null;
    };
    SoundChannel.prototype.axSetPublicProperty = function (value, value2) {
        return null;
    };
    SoundChannel.prototype.axHasPublicProperty = function (value) {
        return null;
    };
    SoundChannel.prototype.axDeletePublicProperty = function (value) {
        return null;
    };
    SoundChannel.prototype.axGetEnumerableKeys = function () {
        return [];
    };
    /* internal */ SoundChannel.prototype.init = function (channel, transform) {
        this._channel = channel;
        if (!this._channel) {
            return;
        }
        this._channel.onSoundComplete = this.soundCompleteInternal.bind(this);
        this.soundTransform = transform;
        SoundMixer._registerSoundSource(this);
    };
    Object.defineProperty(SoundChannel.prototype, "leftPeak", {
        /**
         * The current amplitude (volume) of the left channel, from 0 (silent) to 1 (full amplitude).
         */
        get: function () {
            release || console.log('leftPeak not implemented yet in flash/SoundChannel');
            return 0;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SoundChannel.prototype, "position", {
        /**
         * When the sound is playing, the position property indicates in milliseconds the current point
         * that is being played in the sound file. When the sound is stopped or paused, the
         * position property indicates the last point that was played in the sound file.
         *
         *   A common use case is to save the value of the position property when the
         * sound is stopped. You can resume the sound later by restarting it from that saved position.
         * If the sound is looped, position is reset to 0 at the beginning of each loop.
         */
        get: function () {
            release || console.log('position not implemented yet in flash/SoundChannel');
            return 0;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SoundChannel.prototype, "rightPeak", {
        /**
         * The current amplitude (volume) of the right channel, from 0 (silent) to 1 (full amplitude).
         */
        get: function () {
            release || console.log('rightPeak not implemented yet in flash/SoundChannel');
            return 0;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SoundChannel.prototype, "soundTransform", {
        /**
         * The SoundTransform object assigned to the sound channel. A SoundTransform object
         * includes properties for setting volume, panning, left speaker assignment, and right
         * speaker assignment.
         */
        get: function () {
            //	we not create this in the constructor, because it gets overwritten from Sound in most cases anyway
            //	but we still need a Soundtransform to be available in the getter
            if (!this._soundTransform) {
                this._soundTransform = new this.sec.flash.media.SoundTransform();
            }
            return this._soundTransform;
        },
        set: function (value) {
            if (this._channel) {
                this._channel.volume = value.volume;
                this._channel.pan = value.pan;
            }
            this._soundTransform = value;
        },
        enumerable: true,
        configurable: true
    });
    SoundChannel.prototype.soundCompleteInternal = function () {
        this._stopInternally();
        // we should dispatch sound events to channel, because some games use this
        var complete = new this.sec.flash.events.Event(Event.SOUND_COMPLETE);
        this.dispatchEvent(complete);
    };
    SoundChannel.prototype._stopInternally = function () {
        SoundMixer._unregisterSoundSource(this);
    };
    /**
     * Stops the sound playing in the channel.
     * @langversion	3.0
     * @playerversion	Flash 9
     * @playerversion	Lite 4
     * @refpath
     */
    SoundChannel.prototype.stop = function () {
        if (!this._channel) {
            release || console.log('SoundChannel.stop: No sound exists');
            return;
        }
        this._stopInternally();
        this._channel.stop();
    };
    // for ISoundSource
    SoundChannel.prototype.stopSound = function () {
        this.stop();
    };
    // for ISoundSource
    SoundChannel.prototype.updateSoundLevels = function (volume) {
        if (!this._channel) {
            return;
        }
        this._soundTransform.volume = volume;
        this._channel.volume = volume;
    };
    return SoundChannel;
}(EventDispatcher));
export { SoundChannel };
